import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { RenderedHtml } from 'modules/editor';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';
import { ExamAnswerArea } from './ExamAnswerArea';
import { PassageHighlighter } from './PassageHighlighter';
import { useSaveWord } from 'modules/vocabulary';
import { ResizableSplit } from './ResizableSplit';
import { loadHighlights, saveHighlights, type HighlightsByPassage } from './highlightStore';
import { loadSatModuleState, saveSatModuleState } from './satModuleState';
import { isAnswered } from '../../constants';
import type { CandidateSection } from '../../types';
import { ACTIVE, ANSWERED } from './selection';
import { useQuestionTime } from './useQuestionTime';

interface Props {
  attemptId: string;
  section: CandidateSection;
  answers: Map<string, unknown>;
  onAnswer: (questionId: string, value: unknown) => void;
  /** Seconds spent on a question (a running total) and its review mark, saved with the answers. */
  onMeta?: (questionId: string, meta: { timeSpentSec?: number; flagged?: boolean }) => void;
  /** Lets the session ask for the running time just before it sends. */
  registerSettle?: (settle: () => void) => () => void;
  /** Select passage text to highlight it (Reading and Writing), as in IELTS Reading. */
  annotatorEnabled: boolean;
  eliminatorEnabled: boolean;
  onFinishModule: () => void;
  busy: boolean;
}

/**
 * Digital SAT module: one question at a time. Reading and Writing shows the
 * passage beside the choices, with a divider the student can drag; Math is
 * one column. The last "Next" opens a check-your-work page.
 */
export function SatModuleView({
  attemptId,
  section,
  answers,
  onAnswer,
  onMeta,
  registerSettle,
  annotatorEnabled,
  eliminatorEnabled,
  onFinishModule,
  busy,
}: Props) {
  const { t } = useTranslation();
  const saveWord = useSaveWord();
  const questions = section.questions;
  // A reload returns to the same question, with its review flags and crossed-out options.
  const [saved] = useState(() => loadSatModuleState(attemptId, section.id, questions.map((item) => item.id)));
  const [index, setIndex] = useState(saved?.index ?? 0);
  const [reviewing, setReviewing] = useState(saved?.reviewing ?? false);
  const [flagged, setFlagged] = useState<Set<string>>(() => new Set(saved?.flagged));
  const [eliminatorOn, setEliminatorOn] = useState(false);
  const [eliminated, setEliminated] = useState<Map<string, Set<string>>>(
    () => new Map(Object.entries(saved?.eliminated ?? {}).map(([id, options]) => [id, new Set(options)])),
  );
  useEffect(() => {
    saveSatModuleState(attemptId, section.id, {
      index,
      reviewing,
      flagged: [...flagged],
      eliminated: Object.fromEntries([...eliminated].map(([id, options]) => [id, [...options]])),
    });
  }, [attemptId, section.id, index, reviewing, flagged, eliminated]);
  const [navOpen, setNavOpen] = useState(false);
  const isMath = section.kind === 'SAT_MATH';
  const q = questions[index];

  useQuestionTime(!reviewing ? q?.id : undefined, onMeta, registerSettle);

  // Highlights per question, kept like IELTS Reading's: they survive moving
  // between questions and a reload, and are dropped with the module.
  const [highlights, setHighlights] = useState<HighlightsByPassage>(() => loadHighlights(attemptId, section.id));
  useEffect(() => {
    saveHighlights(attemptId, section.id, highlights);
  }, [attemptId, section.id, highlights]);

  const go = (i: number) => {
    setReviewing(false);
    setIndex(Math.max(0, Math.min(questions.length - 1, i)));
    setNavOpen(false);
  };

  const toggleFlag = (id: string) => {
    const marked = !flagged.has(id);
    setFlagged((prev) => {
      const next = new Set(prev);
      if (marked) next.add(id);
      else next.delete(id);
      return next;
    });
    onMeta?.(id, { flagged: marked });
  };

  const toggleEliminated = useCallback((questionId: string, optionId: string) => {
    setEliminated((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(questionId) ?? []);
      if (set.has(optionId)) set.delete(optionId);
      else set.add(optionId);
      next.set(questionId, set);
      return next;
    });
  }, []);

  const navLabel = reviewing ? t('exam.sat.check_work') : t('exam.nav.question_of', { n: index + 1, total: questions.length });

  const grid = (
    <div className="grid grid-cols-6 gap-2 sm:grid-cols-9">
      {questions.map((item, i) => {
        const answered = isAnswered(answers.get(item.id));
        const isFlagged = flagged.has(item.id);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => go(i)}
            aria-label={t('exam.nav.question_status', {
              n: i + 1,
              status: answered ? t('exam.nav.answered') : t('exam.nav.unanswered'),
            })}
            aria-current={i === index && !reviewing ? 'true' : undefined}
            className={cn(
              'relative flex h-10 items-center justify-center rounded border-2 text-sm font-semibold',
              answered ? ANSWERED : 'border-dashed border-gray-500 bg-white text-gray-800',
            )}
          >
            {i === index && !reviewing && <MapPin className="absolute -top-3 h-3.5 w-3.5 text-gray-900" aria-hidden />}
            {i + 1}
            {isFlagged && <Bookmark className="absolute -right-1.5 -top-1.5 h-3.5 w-3.5 fill-red-600 text-red-600" aria-hidden />}
          </button>
        );
      })}
    </div>
  );

  if (!questions.length) {
    return <p className="p-8 text-center text-gray-600">{t('exam.empty_section')}</p>;
  }

  // Both panes scroll on their own; keyed by question so each one opens at its top.
  const passagePane = (
    <section key={`passage-${q.id}`} aria-label={t('exam.sat.passage')} className="relative min-h-0 flex-1 overflow-y-auto p-5 sm:p-8">
      {annotatorEnabled ? (
        // Select text and a Highlight button appears by it; click a highlight to recolour, note or remove it.
        <PassageHighlighter
          contentKey={q.id}
          highlights={highlights[q.id] ?? []}
          onChange={(update) => setHighlights((prev) => ({ ...prev, [q.id]: update(prev[q.id] ?? []) }))}
          onSaveWord={saveWord}
        >
          <div key={q.id} className="text-[16px] leading-7 text-gray-900">
            <RenderedHtml html={q.prompt || ''} />
          </div>
        </PassageHighlighter>
      ) : (
        <div className="text-[16px] leading-7 text-gray-900">
          <RenderedHtml html={q.prompt || ''} />
        </div>
      )}
    </section>
  );

  const questionPane = (
    <section key={`question-${q.id}`} aria-label={t('exam.sat.question')} className={cn('min-h-0 flex-1 overflow-y-auto p-5 sm:p-8', isMath && 'mx-auto w-full max-w-3xl')}>
      <div className="mb-4 flex items-center gap-3 border-b-2 border-dashed border-gray-300 pb-2">
        <span className="flex h-8 min-w-[2rem] items-center justify-center rounded bg-gray-900 px-2 text-sm font-bold text-white">
          {index + 1}
        </span>
        <button
          type="button"
          onClick={() => toggleFlag(q.id)}
          aria-pressed={flagged.has(q.id)}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-sm text-gray-800 hover:bg-gray-100"
        >
          <Bookmark className={cn('h-4 w-4', flagged.has(q.id) && 'fill-red-600 text-red-600')} aria-hidden />
          {t('exam.nav.mark_review')}
        </button>
        {eliminatorEnabled && (q.type === 'MULTIPLE_CHOICE' || q.type === 'MULTIPLE_RESPONSE') && (
          <button
            type="button"
            onClick={() => setEliminatorOn((v) => !v)}
            aria-pressed={eliminatorOn}
            aria-label={t('exam.sat.eliminator')}
            className={cn(
              'ml-auto rounded border px-2 py-1 text-xs font-semibold line-through',
              eliminatorOn ? ACTIVE : 'border-gray-400 text-gray-700 hover:bg-gray-100',
            )}
          >
            ABC
          </button>
        )}
      </div>
      {isMath && (
        <div className="mb-5 text-[16px] leading-7 text-gray-900">
          <RenderedHtml html={q.prompt || ''} />
        </div>
      )}
      <ExamAnswerArea
        question={q}
        value={answers.get(q.id)}
        onChange={(v) => onAnswer(q.id, v)}
        eliminatorOn={eliminatorOn}
        eliminated={eliminated.get(q.id)}
        onToggleEliminated={(optionId) => toggleEliminated(q.id, optionId)}
      />
    </section>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">

        {reviewing ? (
          <div className="mx-auto w-full max-w-3xl overflow-y-auto p-6 sm:p-10">
            <h2 className="text-center text-2xl font-semibold text-gray-900">{t('exam.sat.check_work')}</h2>
            <p className="mt-2 text-center text-sm text-gray-600">{t('exam.sat.check_work_help')}</p>
            <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
              <div className="mb-4 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded-sm border-2 border-dashed border-gray-500" /> {t('exam.nav.unanswered')}</span>
                <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded-sm border border-gray-900 bg-gray-900" /> {t('exam.nav.answered')}</span>
                <span className="flex items-center gap-1.5"><Bookmark className="h-3.5 w-3.5 fill-red-600 text-red-600" /> {t('exam.nav.for_review')}</span>
              </div>
              {grid}
            </div>
          </div>
        ) : isMath ? (
          questionPane
        ) : (
          <ResizableSplit handleLabel={t('exam.resize_panes')} start={passagePane} end={questionPane} />
        )}
      </div>

      {/* On phones the navigator label truncates and Back shows only its arrow, so the row never scrolls sideways. */}
      <footer className="flex min-w-0 items-center justify-between gap-2 border-t-2 border-dashed border-gray-300 bg-[#F5F7FB] px-3 py-3 sm:gap-3 sm:px-6">
        <span className="hidden text-sm font-medium text-gray-700 sm:inline">{section.title}</span>
        <Popover open={navOpen} onOpenChange={setNavOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              title={navLabel}
              className="min-w-0 flex-1 border-gray-900 bg-gray-900 text-white hover:bg-gray-800 hover:text-white sm:flex-none"
            >
              <span className="truncate">{navLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(92vw,420px)]" align="center">
            <p className="mb-3 text-sm font-semibold text-gray-900">{section.title}</p>
            {grid}
            <Button variant="outline" className="mt-4 w-full" onClick={() => { setReviewing(true); setNavOpen(false); }}>
              {t('exam.sat.go_review')}
            </Button>
          </PopoverContent>
        </Popover>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            onClick={() => (reviewing ? go(questions.length - 1) : go(index - 1))}
            disabled={!reviewing && index === 0}
            title={t('exam.nav.back')}
            className="max-sm:w-9 max-sm:px-0"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden /> <span className="max-sm:sr-only">{t('exam.nav.back')}</span>
          </Button>
          {reviewing ? (
            <Button onClick={onFinishModule} disabled={busy}>
              {t('exam.nav.next')} <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          ) : (
            <Button onClick={() => (index === questions.length - 1 ? setReviewing(true) : go(index + 1))}>
              {t('exam.nav.next')} <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}

export default SatModuleView;
